import * as React from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { SizedTextDataInput } from "../../textDataInput/TextDataInput";
import { SizedDropdownDataInput } from "../../dropdownDataInput/DropdownDataInput";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { IMembersPortalField } from "../../../../membersportal/types/IMembersPortalField";
import { EMPTY_STRING_VALUE } from "../../../Consts";
import DisplayTableSearchResults from "../../../../membersportal/sharedComponents/displayTableSearchResults/DisplayTableSearchResults";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import ActionButton from "../../actionButton/ActionButton";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { useMsal } from "@azure/msal-react";
import { IClaimUnidentifiedClaimModal } from "../../../../membersportal/types/IClaimUnidentifiedClaimModal";
import DisplayText from "../../text/DisplayText";
import { ICueSheetsSearchQuery } from "../../../../membersportal/types/ICueSheetsSearchQuery";
import { IRegisteredSetListsSearchQuery } from "../../../../membersportal/types/IRegisteredSetListsSearchQuery";
import { SEARCH_VIEW_MEMBER_UNPAID_CLAIMS_PRODUCT_SEARCH } from "../../../../membersportal/Consts";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { getLookupValues } from "../../../../lookup/services/LookupHelpers";
import { AV_CATEGORY_LOOKUP } from "../../../../lookup/Consts";
import SpinnerPopUp from "../../spinner/SpinnerPopUp";
import { MEMBERS_PORTAL } from "../../../../accounts/Consts";

const CUE_SHEET = 'CUE SHEET';
const SET_LIST = 'SET LIST';
const CUE_SHEET_ONLY_FIELDS = ['cueSheetID', 'productionType'];
const SET_LIST_ONLY_FIELDS = ['setListID'];

export interface UnpaidClaimsProductSearchModalViewProps {
    searchCueSheets: (query: ICueSheetsSearchQuery) => void;
    searchSetLists: (query: IRegisteredSetListsSearchQuery) => void;
    searchViewData: IRepertoireComponentDataItem;
    searchResultsTableData: IRepertoireComponentDataItem;
    cueSheetsSearchResults: any;
    setListsSearchResults: any;
    searchSuccessful: boolean;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    resetPagination: (repertoireSection: string) => void;
    updatePagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    currentUserNameNumber?: string;
    modalTitle: string;
    hideModal: () => void;
    claimUnpaidUsage: (query: IClaimUnidentifiedClaimModal) => void;
    usage: any;
    fields: IMembersPortalField[];
    lookupValues: ILookupDictionary;
    isSearching?: boolean;
    clearProductSearch?: () => void;
}

const UnpaidClaimsProductSearchModalView: React.FC<UnpaidClaimsProductSearchModalViewProps> = ({
    searchCueSheets,
    searchSetLists,
    searchViewData,
    searchSuccessful,
    searchResultsTableData,
    cueSheetsSearchResults,
    setListsSearchResults,
    indexOfFirstResult,
    indexOfLastResult,
    resultsPerPage,
    resetPagination,
    updatePagination,
    hideModal,
    modalTitle,
    claimUnpaidUsage,
    currentUserNameNumber,
    usage,
    fields,
    lookupValues,
    isSearching,
    clearProductSearch,
}) => {
    const componentInstance = SEARCH_VIEW_MEMBER_UNPAID_CLAIMS_PRODUCT_SEARCH;
    const productType: string = usage?.productType?.toUpperCase() ?? '';
    const isCueSheet = productType === CUE_SHEET;
    const isSetList = productType === SET_LIST;

    const [loaded, setLoaded] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const { instance, accounts } = useMsal();

    const [formData, setFormData] = React.useState({
        title: '',
        cueSheetID: '',
        setListID: '',
        productionType: '',
    });

    React.useEffect(() => {
        setLoaded(true);
        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
        resetPagination(componentInstance);
        clearProductSearch && clearProductSearch();
    }, [])

    const hiddenFields = isCueSheet ? SET_LIST_ONLY_FIELDS : CUE_SHEET_ONLY_FIELDS;

    const filteredFields = (searchViewData?.fields ?? []).filter(
        (searchField: IRepertoireField | IMembersPortalField) =>
            searchField.componentInstance === componentInstance && !hiddenFields.includes(searchField.name)
    );

    const filteredSearchResults = (searchResultsTableData?.fields ?? []).filter(
        (field: IRepertoireField) =>
            field.componentInstance === componentInstance && !hiddenFields.includes(field.name)
    );

    const updateUserPagination = (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => {
        updatePagination(indexOfFirstResult, indexOfLastResult, resultsPerPage, currentPage, repertoireSection);
        setCurrentPage(currentPage);
    }

    const changeData = (value: any, fieldName: IRepertoireStateKeys) => {
        setFormData(
            { ...formData, [fieldName]: value }
        )
    }

    const keyDownSubmit = (value: any) => {
        if (value.key === 'Enter') {
            onClickSearch();
        }
    }

    const onClickSearch = () => {
        if (isCueSheet) {
            searchCueSheets({
                cueSheetID: formData.cueSheetID,
                title: formData.title,
                status: null,
                dataSource: 'Repertoire',
                currentUserNameNumber: currentUserNameNumber,
                productionType: formData.productionType || null,
                includeCueSheetsWithMyRegisteredWorks: true,
            });
        } else if (isSetList) {
            searchSetLists({
                setListID: formData.setListID,
                title: formData.title,
                status: null,
                dataSource: 'Repertoire',
                currentUserNameNumber: currentUserNameNumber,
            });
        }
    };

    const rowActions = {
        textButton: {
            text: 'Select',
            onClick: (product) => {
                const query = {
                    usageGroupId: usage.id,
                    claimedProductId: product.productCoreID,
                    claimantCurrentUserNameNumber: currentUserNameNumber,
                }
                claimUnpaidUsage(query);
                hideModal();
            }
        }
    }

    const renderSearchFields = () => {
        let filteredSearchFieldsMapper = (searchField: IRepertoireField | IMembersPortalField, index: number): React.ReactElement => {
            let formField: React.ReactElement = <></>;

            if (searchField.name === 'productType') {
                formField =
                    <div key={index}>
                        <SizedTextDataInput
                            label={searchField.data}
                            fieldName={searchField.name}
                            changeData={() => { }}
                            value={productType}
                            readOnly={true}
                            isHidden={false}
                        />
                    </div>
            } else if (searchField.name === 'productionType') {
                formField =
                    <div key={index}>
                        <SizedDropdownDataInput
                            label={searchField.data}
                            fieldName={searchField.name}
                            changeData={changeData}
                            value={formData.productionType}
                            options={getLookupValues(AV_CATEGORY_LOOKUP, lookupValues)}
                            allowNull={true}
                            isHidden={false}
                            componentInstance={componentInstance}
                            readOnly={false}
                            portalType={MEMBERS_PORTAL}
                        />
                    </div>
            } else if (searchField.fieldType === 'text') {
                formField =
                    <div key={index}>
                        <SizedTextDataInput
                            fieldType={searchField.fieldType}
                            useEnterSubmit={true}
                            label={searchField.data}
                            fieldName={searchField.name}
                            changeData={(value, fieldName) => changeData(value, fieldName)}
                            handleKeyDown={keyDownSubmit}
                            value={searchField.name === 'values' ? EMPTY_STRING_VALUE : formData[searchField.name]}
                            isHidden={false}
                        />
                    </div>
            }

            return formField;
        };
        return filteredFields.map(filteredSearchFieldsMapper);
    };

    const titleField = fields.find(field => field.name === (isCueSheet ? "registeredCueSheetSearchTitle" : "registeredSetListSearchTitle"));
    const unsupportedProductTypeField = fields.find(field => field.name === "unsupportedProductTypeMessage");
    const searchResults = isCueSheet ? cueSheetsSearchResults : setListsSearchResults;

    if (loaded) {
        return (
            <div className="repertoireModal">
                <SpinnerPopUp visible={isSearching} />
                <div className={"modal modal--large"}>
                    <div className='modal-header' style={{
                        background: "#38429C",
                        color: "white",
                    }}>
                        <button type="button" className="close" data-dismiss="modal" aria-hidden="true" onClick={() => hideModal()}>
                            <Dismiss20Regular title="Close" />
                        </button>
                        <h1 className="modal-title">{modalTitle}</h1>
                    </div>
                    <div className="fixedPart" />
                    <div className="dynamicPart">
                        <div className="searchView">
                            {!isCueSheet && !isSetList ?
                                <div key='unsupportedProductType' className="row">
                                    <div className="paddingVeryLarge"><DisplayText text={unsupportedProductTypeField?.data} /></div>
                                </div>
                                : <>
                                    <div key='searchFields' className="row">
                                        <div className="paddingVeryLarge"><DisplayText text={titleField?.data} /></div>
                                        {renderSearchFields()}
                                    </div>
                                    <div key='action' className="row">
                                        <div className="form-group col-lg-4 col-lg-push-8 col-md-4 col-md-push-8 col-sm-6 col-sm-push-6 col-xs-12 col-xs-push-0">
                                            <ActionButton buttonText="Search" buttonAction={onClickSearch} />
                                        </div>
                                    </div>
                                    {searchSuccessful && searchResults && <div key='results' className="resultsScrollSection">
                                        <div className="resultsScrollSectionArea">
                                            <DisplayTableSearchResults
                                                searchSuccessful={searchSuccessful}
                                                searchResultsTableData={filteredSearchResults}
                                                searchResults={searchResults}
                                                indexOfFirstResult={indexOfFirstResult}
                                                indexOfLastResult={indexOfLastResult}
                                                resultsPerPage={resultsPerPage}
                                                currentPage={currentPage}
                                                updatePagination={updateUserPagination}
                                                resetPagination={resetPagination}
                                                rowActions={rowActions}
                                                repertoireSection={componentInstance}
                                            />
                                        </div>
                                    </div>}
                                </>}
                        </div>
                    </div>
                </div>
                <div className="fixedPart" />
            </div>
        );
    }
    return <div>Loading ...</div>
}

export default UnpaidClaimsProductSearchModalView;
